import React, { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { isDesktop, notifyNewMessage } from "desktop";
import { I18n, useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import emitter, { EVENTS, SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import { cleanMessagePreview } from "lib/messageMetadata";
import providers from "lib/providers";
import { isNotifiableSystemMessage } from "lib/systemMessageText";
import { NotificationFactory } from "../../index";

// Module-scoped TTL cache for the per-message thread lookup. A burst of group
// messages from the same thread would otherwise hit the GraphQL endpoint once
// per message (Apollo is configured with fetchPolicy "no-cache" globally).
// Keyed by threadId; entries expire after 30s which is short enough to pick up
// group rename / channel toggle quickly, long enough to dedupe normal bursts.
const THREAD_LOOKUP_TTL_MS = 30_000;
const threadLookupCache = new Map<
  string,
  {
    at: number;
    subjectType: string | null;
    groupSlug: string | null;
    groupName: string | null;
    subjectId: string | null;
    topicId: string | null;
  }
>();

function MessageManager({ sendNotification }: { sendNotification: NotificationFactory }) {
  const { user } = useAuthUser();
  const { t } = useTranslation();
  const userRef = useRef(user);
  const pathName = useRef("");
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    userRef.current = user;
    pathName.current = location.pathname;
  }, [user, location]);

  // Drop cached thread metadata when the session ends so a re-login (potentially
  // as a different account) doesn't surface stale group/personal classifications
  // within the 30s TTL window. The cache is purely a hot-path dedup, not a
  // source of truth, so wiping it is always safe.
  useEffect(() => {
    if (!user) threadLookupCache.clear();
  }, [user]);

  useEffect(() => {
    let unsubscribe: () => void;
    if (!user) {
      return;
    }
    providers.MessagesProvider.subscribeToAllThreads(async (message) => {
      emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, message);

      // Cheap filters first — avoid the thread lookup for self/system messages.
      // System messages (call lifecycle / recording state / membership) carry a
      // real author id, so guard on the text prefix too — otherwise they raised
      // a "new message" toast + OS-fallback the recipient can't act on. Two
      // classes stay notifiable (isNotifiableSystemMessage): the group
      // call-start announcement (the only ring channel for group calls) and
      // post-call artifact links (recording/transcript/insights).
      if (
        parseInt(message.senderId) < 1 ||
        !userRef.current ||
        message.senderId === userRef.current.id ||
        (message.text?.startsWith(SYSTEM_MESSAGE_PREFIX) && !isNotifiableSystemMessage(message.text))
      ) {
        return;
      }

      // Resolve route: group threads link to /messages/group/<slug|id>, personal
      // to /messages/<userSlug|id>. Without this lookup the banner click would
      // open the sender's DM even for group messages — same bug fixed for the
      // Web Push SW and Capacitor mobile tap handler in earlier commits.
      let url = `/messages/${message.senderUser?.slug || message.senderId}`;
      let groupName: string | null = null;
      const threadKey = String(message.threadId);
      let cached = threadLookupCache.get(threadKey);
      if (!cached || Date.now() - cached.at > THREAD_LOOKUP_TTL_MS) {
        try {
          const resp = await providers.MessageThreadsProvider.find({
            data: { ids: [threadKey], userId: String(userRef.current.id), size: 1 },
          });
          const thread = resp.result?.[0];
          cached = {
            at: Date.now(),
            subjectType: thread?.subject?.subjectType ?? null,
            groupSlug: thread?.groupSlug ?? null,
            groupName: thread?.groupName ?? null,
            subjectId: thread?.subject?.subjectId ?? null,
            topicId: thread?.topicId ?? null,
          };
          threadLookupCache.set(threadKey, cached);
        } catch {
          /* fall through to personal URL */
        }
      }
      if (cached?.subjectType === "group") {
        const slug = cached.groupSlug || cached.subjectId;
        // Guard against a missing slug/id (double API failure) so we never build a
        // "/messages/group/undefined" route for the desktop notification tap.
        if (slug) {
          url = `/messages/group/${slug}`;
          // Group Topics (Темы): deep-link the banner into the specific topic.
          if (cached.topicId) url += `/topic/${cached.topicId}`;
          groupName = cached.groupName;
        }
      }

      // Suppress the toast if the user is already viewing this chat.
      if (url === pathName.current) return;

      const senderScreenName = message.senderUser?.screenName ?? "";
      // For groups, prefix the displayed sender with the group name so the user
      // sees "AnimarMedia: Senyra" instead of just "Senyra" — matches the title
      // format used by the mobile/native push payload.
      const displayName = groupName ? `${groupName}: ${senderScreenName}` : senderScreenName;

      sendNotification({
        text: (
          <I18n
            path={"You've got new message from <author>{{screenName}}</author>"}
            components={{
              author: <strong style={{ whiteSpace: "nowrap" }} />,
            }}
            values={{ screenName: displayName }}
          />
        ),
        image: {
          icon: "messages",
        },
        onClick: () => history.push(url),
      });

      // OS-level fallback when the tab is not visible: fires regardless of whether
      // Web Push / FCM is configured, since we already have the message via WS.
      // Uses the SAME tag scheme as the service worker — msg-{groupId}-{topicId}
      // for group Topics, else msg-{threadId} — so a SW push for the same
      // (topic) thread replaces (not duplicates) this notification, and distinct
      // topics in one group stay as separate notifications.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        const titleName = groupName
          ? `${groupName}: ${senderScreenName || t("New message")}`
          : senderScreenName || t("New message");
        // Clean internal markers (__SYSTEM__:, __META__:{json}, [[...]], URLs) and
        // label voice/round-video — same rules as the backend push body builder.
        const body = cleanMessagePreview(message.text, t);
        // Reuse the already-resolved deep-link path (which includes the topic
        // segment for group Topics) so the desktop tap lands on the same chat
        // as the in-app banner — `onNotificationAction` navigates to
        // /messages/${chatId} verbatim.
        const chatId = url.startsWith("/messages/") ? url.slice("/messages/".length) : url;
        // Notification tag MUST match the service worker's (push-sw.js tags
        // msg-{threadId} unconditionally) so an SW web-push and this WS fallback
        // for the same message replace each other instead of double-notifying.
        // Topics stay distinct without a topic suffix — every topic is its own
        // thread, so threadId is already topic-unique.
        const notifTag = `msg-${message.threadId || message.senderId || ""}`;
        if (isDesktop) {
          notifyNewMessage({ senderName: titleName, text: body, chatId }).catch(() => {});
        } else if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            const n = new Notification(titleName, {
              body,
              tag: notifTag,
              icon: "/logo192.png",
            });
            n.onclick = () => {
              window.focus();
              history.push(url);
              n.close();
            };
          } catch {
            // Some browsers throw on Notification() when service worker is required.
          }
        }
      }
    }).then((u) => (unsubscribe = () => u.unsubscribe()));
    return () => unsubscribe && unsubscribe();
  }, [history, sendNotification, t, user]);

  return null;
}

export default MessageManager;
